import {
  MarkovTrainRequest,
  MarkovTrainResponse,
  MarkovSignal,
  WalkForwardResult,
  MarkovDiagnostics,
  MarkovHealth,
  TradingAgent,
  AgentDecision,
  FishbowlDashboard,
} from '@/types/markov.types';

// API Configuration
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001';
const MARKOV_BASE = `${API_BASE_URL}/markov`;

// Helper for authenticated requests
async function fetchWithAuth<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

// Markov V2 API Client
export const markovApi = {
  // Training
  async train(params: MarkovTrainRequest): Promise<MarkovTrainResponse> {
    const queryParams = new URLSearchParams({
      instrument: params.instrument,
      timeframe: params.timeframe,
      ...(params.lookback_days && { lookback_days: params.lookback_days.toString() }),
      ...(params.num_states && { num_states: params.num_states.toString() }),
      ...(params.min_samples_per_state && {
        min_samples_per_state: params.min_samples_per_state.toString()
      }),
      ...(params.kelly_fraction && { kelly_fraction: params.kelly_fraction.toString() }),
    });

    return fetchWithAuth<MarkovTrainResponse>(
      `${MARKOV_BASE}/v2/train?${queryParams}`,
      { method: 'POST' }
    );
  },

  // Get trading signal
  async getSignal(instrument: string, timeframe: string): Promise<MarkovSignal> {
    const queryParams = new URLSearchParams({ instrument, timeframe });
    return fetchWithAuth<MarkovSignal>(`${MARKOV_BASE}/v2/signal?${queryParams}`);
  },

  // Walk-forward validation
  async walkForward(
    instrument: string,
    timeframe: string,
    trainBars: number = 5000,
    testBars: number = 1000,
    numFolds: number = 5
  ): Promise<WalkForwardResult> {
    const queryParams = new URLSearchParams({
      instrument,
      timeframe,
      train_bars: trainBars.toString(),
      test_bars: testBars.toString(),
      num_folds: numFolds.toString(),
    });

    return fetchWithAuth<WalkForwardResult>(
      `${MARKOV_BASE}/v2/walk-forward?${queryParams}`,
      { method: 'POST' }
    );
  },

  // Get diagnostics
  async getDiagnostics(instrument: string, timeframe: string): Promise<MarkovDiagnostics> {
    const queryParams = new URLSearchParams({ instrument, timeframe });
    return fetchWithAuth<MarkovDiagnostics>(`${MARKOV_BASE}/v2/diagnostics?${queryParams}`);
  },

  // Health check
  async getHealth(): Promise<MarkovHealth> {
    return fetchWithAuth<MarkovHealth>(`${MARKOV_BASE}/health`);
  },
};

// Agent API Client
export const agentApi = {
  // List all agents (authenticated)
  async listAgents(): Promise<TradingAgent[]> {
    return fetchWithAuth<TradingAgent[]>(`${API_BASE_URL}/agents/list`);
  },

  // Get agent by ID
  async getAgent(agentId: string): Promise<TradingAgent> {
    return fetchWithAuth<TradingAgent>(`${API_BASE_URL}/agents/${agentId}`);
  },

  // Create new agent
  async createAgent(data: Partial<TradingAgent>): Promise<TradingAgent> {
    return fetchWithAuth<TradingAgent>(`${API_BASE_URL}/agents/create`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Start agent
  async startAgent(agentId: string): Promise<{ status: string }> {
    return fetchWithAuth<{ status: string }>(`${API_BASE_URL}/agents/${agentId}/start`, {
      method: 'POST',
    });
  },

  // Stop agent
  async stopAgent(agentId: string): Promise<{ status: string }> {
    return fetchWithAuth<{ status: string }>(`${API_BASE_URL}/agents/${agentId}/stop`, {
      method: 'POST',
    });
  },

  // Get agent decisions
  async getDecisions(agentId: string, limit: number = 50): Promise<AgentDecision[]> {
    return fetchWithAuth<AgentDecision[]>(
      `${API_BASE_URL}/agents/${agentId}/decisions?limit=${limit}`
    );
  },

  // Get agent performance
  async getPerformance(agentId: string): Promise<any> {
    return fetchWithAuth(`${API_BASE_URL}/agents/${agentId}/performance`);
  },
};

// Public Fishbowl API (no auth required)
export const fishbowlApi = {
  async getDashboard(): Promise<FishbowlDashboard> {
    return fetch(`${API_BASE_URL}/public/fishbowl/dashboard`).then(res => res.json());
  },

  async getAgents(): Promise<TradingAgent[]> {
    return fetch(`${API_BASE_URL}/public/fishbowl/agents`).then(res => res.json());
  },

  async getActivity(limit: number = 50): Promise<AgentDecision[]> {
    return fetch(`${API_BASE_URL}/public/fishbowl/activity?limit=${limit}`)
      .then(res => res.json());
  },

  async getLeaderboard(): Promise<any[]> {
    return fetch(`${API_BASE_URL}/public/fishbowl/leaderboard`).then(res => res.json());
  },
};

// WebSocket helper
export function createFishbowlWebSocket(onMessage: (data: FishbowlDashboard) => void) {
  const wsUrl = API_BASE_URL.replace('http', 'ws') + '/public/fishbowl/live';
  const ws = new WebSocket(wsUrl);

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage(data);
    } catch (error) {
      console.error('WebSocket message parse error:', error);
    }
  };

  ws.onerror = (error) => {
    console.error('WebSocket error:', error);
  };

  return ws;
}
